import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import ContentstackLivePreview from '@contentstack/live-preview-utils';
import { useLocation } from '@reach/router';
import { ContentstackGatsby } from 'gatsby-source-contentstack/live-preview';

import AnalyticsService from 'services/analyticsService';
import { Logger } from 'services/logger';
import { ONETRUST_CATEGORIES, OneTrustService } from 'services/oneTrust';
import { PersonalizeService } from 'services/personalize';
import { PersonalizeSDKError, PersonalizeSDKService } from 'services/personalize/sdkService';

import { getExperienceAndVariantFromAlias, triggerEvent } from 'utils/contentstack/personalize';
import { isLivePreviewEnabled, isPersonalizeEnabled } from 'utils/featureFlags';

import { getCSData } from '../live-preview';

export const AB_TEST_EVENTS = {
  BUTTON_CLICK: '[Block] Button: Click',
};

const ContentstackContext = createContext(null);

export const ContentstackProvider = ({
  children,
  pageData,
  abTestConfiguration,
  personalizeData: initialPersonalizeData,
}) => {
  const [data, setData] = useState(pageData);
  const [sdk, setSdk] = useState(null);
  const [personalizeData, setPersonalizeData] = useState({
    alias: null,
    variant: null,
    experience: null,
    experienceShortUid: null,
    variantShortUid: null,
  });
  const [isPersonalizeDataSettled, setIsPersonalizeDataSettled] = useState(false);
  const [clickEventTriggers, setClickEventTriggers] = useState(abTestConfiguration?.click_event_triggers || []);

  const { search } = useLocation();

  const fetchLivePreviewData = async () => {
    try {
      const updatedData = await getCSData.get(data);
      setData(updatedData);
    } catch (error) {
      Logger.error('Error fetching live preview data:', error);
    }
  };

  const loadPersonalizeData = async (personalizeSdk) => {
    if (!pageData?.uid) return;

    try {
      const personalizedData = await PersonalizeService.fetchPersonalizeData(
        personalizeSdk,
        pageData.uid,
        search,
        initialPersonalizeData
      );

      if (!personalizedData) {
        return;
      }

      const { experienceShortUid, variantShortUid } = getExperienceAndVariantFromAlias(personalizedData.alias);

      setData(personalizedData.data);
      setPersonalizeData({
        alias: personalizedData.alias,
        variant: personalizedData.data?.variant_name,
        experience: personalizedData.data?.experience_name,
        experienceShortUid,
        variantShortUid,
      });

      void personalizeSdk.triggerImpression(experienceShortUid);

      AnalyticsService.pushEvent('ab_test_variation_viewed', {
        variation_alias: personalizedData.alias,
        variation_id: experienceShortUid,
        experience_name: personalizedData.data?.experience_name,
        variation_name: personalizedData.data?.variant_name,
        active_variant: variantShortUid,
      });

      Logger.log('Trigger Personalize impression: ', personalizedData.alias);
    } catch (err) {
      Logger.error('Error initializing Personalize: ', err);
    }
  };

  const triggerPersonalizeEvent = useCallback(
    (eventName, uid) => {
      if (!isPersonalizeEnabled || !sdk) return;

      const trigger = clickEventTriggers.find((trigger) => trigger.uid === uid);
      if (!trigger) return;

      Logger.log(`Trigger Conversion Event: ${eventName} for element with UID: ${uid}`);
      AnalyticsService.pushEvent('ab_test_conversion', {
        conversion_action: eventName,
        variation_alias: personalizeData.alias,
        variation_id: personalizeData.experienceShortUid,
        experience_name: personalizeData.experience,
        variation_name: personalizeData.variant,
        active_variant: personalizeData.variantShortUid,
      });
      void triggerEvent(eventName, sdk);
    },
    [sdk, clickEventTriggers, personalizeData]
  );

  useEffect(() => {
    if (pageData) {
      ContentstackGatsby.addContentTypeUidFromTypename(pageData);
    }
    // Setup live preview
    if (isLivePreviewEnabled) {
      ContentstackLivePreview.onEntryChange(fetchLivePreviewData);
    }

    // Initialize personalization SDK
    const initializePersonalize = async () => {
      try {
        if (!isPersonalizeEnabled) {
          return; // Personalization globally disabled
        }

        const OneTrustConsent = await OneTrustService.checkConsent(ONETRUST_CATEGORIES.targeting);
        if (!OneTrustConsent) {
          return; // No user consent
        }

        const sdkInstance = await PersonalizeSDKService.waitForSdk();

        setSdk(sdkInstance);
        await loadPersonalizeData(sdkInstance);
      } catch (error) {
        if (error instanceof PersonalizeSDKError) {
          Logger.warn('Personalize SDK initialization failed:', error.message);
          return;
        }
        Logger.error('Error initializing personalization:', error);
      } finally {
        PersonalizeSDKService.clearTimer();
        setIsPersonalizeDataSettled(true);
      }
    };

    void initializePersonalize();

    return () => {
      if (isLivePreviewEnabled) {
        ContentstackLivePreview?.unsubscribeOnEntryChange(fetchLivePreviewData);
      }
    };
  }, []);

  useEffect(() => {
    setClickEventTriggers(abTestConfiguration?.click_event_triggers || []);
  }, []);

  const contextValue = useMemo(
    () => ({
      data,
      sdk,
      personalize: personalizeData,
      isPersonalizeDataSettled: isPersonalizeDataSettled,
      triggerPersonalizeEvent,
      shouldWaitForPersonalize: abTestConfiguration?.wait_for_personalize || false,
    }),
    [data, sdk, personalizeData, isPersonalizeDataSettled, triggerPersonalizeEvent]
  );

  return <ContentstackContext.Provider value={contextValue}>{children}</ContentstackContext.Provider>;
};

export const useContentstack = () => {
  const context = useContext(ContentstackContext);

  if (!context) {
    Logger.error('useContentstack must be inside ContentstackProvider');
    return {
      data: null,
      sdk: null,
      personalize: { alias: null, variant: null, experience: null, experienceShortUid: null, variantShortUid: null },
      isPersonalizeDataSettled: false,
      shouldWaitForPersonalize: false,
      triggerPersonalizeEvent: () => {},
    };
  }

  return context;
};
